/**
 * Reports, Return Lines (migration 0092; the #55 plan's §B.20): the lines of
 * the annual return the business files, for the fiscal year the period ends
 * in, each opening to its accounts. A prompt for the CPA, never a filing.
 *
 * Nothing is dropped: an account with a figure and no line is listed under
 * Not On Any Line, and a line the form has since relabelled under Needs
 * Confirming (round 168's RL-3), each with its figures, and the ties say how
 * much is unplaced. Other Changes In Net Assets (RL-1) lists each entry, so
 * an opening entry reads as itself. Edit Lines is for whoever manages the
 * business; the figures are the API's.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  api,
  type ReturnColumn,
  type ReturnColumns,
  type ReturnLineForm,
  type ReturnLineMap,
  type ReturnLinesReport,
  type ReturnReportLine,
} from '../api.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';

export const FORM_TITLES: Record<ReturnLineForm, string> = { '990_pf': 'Form 990-PF', '990': 'Form 990', '990_ez': 'Form 990-EZ' };

const COLUMN_TITLES: Record<ReturnColumn, string> = {
  a: '(a) Per Books',
  b: '(b) Net Investment Income',
  c: '(c) Adjusted Net Income',
  d: '(d) Charitable, Cash Basis',
  start: 'Beginning Of Year',
  end: 'End Of Year',
  amount: 'Amount',
  total: '(A) Total',
  program: '(B) Program Services',
  management: '(C) Management And General',
  fundraising: '(D) Fundraising',
  membership: 'Membership Development',
};

const amount = (value: number | null | undefined) => (value === null || value === undefined ? '' : formatCents(value));
const balanceKinds = new Set(['asset', 'liability']);

/**
 * What an unplaced account holds, as the ties count it: a balance at the year's end, or the year's figure.
 * Both lists show it, so every amount the unplaced notice says is traceable to a row (round 170's RL-5).
 */
const unplacedAmount = (account: { type: string; values: ReturnColumns }) =>
  balanceKinds.has(account.type) ? amount(account.values.end) : amount(account.values.a ?? account.values.total ?? account.values.amount);

/** Where an account's figure drills down to: the year for a revenue or expense line, the end date for a balance. */
function linesHref(accountId: string, report: ReturnLinesReport, balance: boolean): string {
  const params = new URLSearchParams({ account: accountId, report: 'returns', to: report.to });
  if (!balance) params.set('from', report.from);
  return `/reports/account?${params.toString()}`;
}

function Cells({ columns, values }: { columns: ReturnColumn[]; values: ReturnColumns }) {
  return (
    <>
      {columns.map((column) => (
        <td key={column} className="num">
          {amount(values[column])}
        </td>
      ))}
    </>
  );
}

function PartTable({ report, part }: { report: ReturnLinesReport; part: ReturnLinesReport['parts'][number] }) {
  const lines = report.lines.filter((line) => line.part === part.part);
  const row = (line: ReturnReportLine) => {
    const balance = balanceKinds.has(line.kind);
    return (
      <Fragment key={line.line}>
        <tr className={line.kind === 'total' ? 'report-total' : undefined}>
          <td>{line.line}</td>
          <td>
            {line.label}
            {line.note && (
              <>
                {' '}
                <InfoNote label={`Why Line ${line.line} Is Blank`} anchored>
                  {line.note}
                </InfoNote>
              </>
            )}
          </td>
          <Cells columns={part.columns} values={line.values} />
        </tr>
        {line.rows
          ? line.rows.map((described) => (
              <tr key={described.line} className="return-account">
                <td>{described.line}</td>
                <td>{described.label}</td>
                <Cells columns={part.columns} values={described.values} />
              </tr>
            ))
          : line.accounts.map((account) => (
              <tr key={account.accountId} className="return-account">
                <td />
                <td>
                  <Link to={linesHref(account.accountId, report, balance)}>
                    {account.code} {account.name}
                  </Link>
                </td>
                <Cells columns={part.columns} values={account.values} />
              </tr>
            ))}
      </Fragment>
    );
  };
  return (
    <>
      <h3>
        Part {part.part}: {part.title}
      </h3>
      <div className="table-scroll">
        <table className="lines" aria-label={`Part ${part.part}`}>
          <thead>
            <tr>
              <th>Line</th>
              <th>Description</th>
              {part.columns.map((column) => (
                <th key={column} className="num">
                  {COLUMN_TITLES[column]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{lines.map(row)}</tbody>
        </table>
      </div>
    </>
  );
}

/** Edit Lines: each account's line on the form, offering only lines an account of its type may be on. */
function LineEditor({ form, onChanged }: { form: ReturnLineForm; onChanged: () => void }) {
  const [map, setMap] = useState<ReturnLineMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const read = useCallback(() => {
    api
      .returnLineMap(form)
      .then(setMap)
      .catch((caught: unknown) => setError(caught instanceof ApiError ? caught.message : 'Could not read the lines.'));
  }, [form]);
  useEffect(read, [read]);

  if (map === null) return error ? <p role="alert" className="error">{error}</p> : null;
  const fits = (kind: string, type: string) => (kind === 'less' ? type === 'expense' : kind === type);

  const choose = (accountId: string, value: string) => {
    const [part, line] = value === '' ? [null, null] : (value.split(':') as [string, string]);
    setError(null);
    setNotice(null);
    api
      .setReturnLine(form, accountId, part, line)
      .then(() => {
        read();
        onChanged();
      })
      .catch((caught: unknown) => setError(caught instanceof ApiError ? caught.message : 'Could not save the line.'));
  };

  const suggest = () => {
    setError(null);
    api
      .applySuggestedReturnLines(form)
      .then(({ written }) => {
        setNotice(written === 0 ? 'Every account with a suggestion already has a line.' : `${written} suggested lines written. Check each with your CPA.`);
        read();
        onChanged();
      })
      .catch((caught: unknown) => setError(caught instanceof ApiError ? caught.message : 'Could not write the suggestions.'));
  };

  return (
    <section className="return-line-editor" aria-label="Edit Lines">
      <p className="muted">
        Lines of the {map.catalogueYear} {FORM_TITLES[form]}. Saving a line confirms it against that form.{' '}
        <InfoNote label="What A Suggested Line Is">
          For each account the non-profit chart starts with, the line it most often goes on. It is written only when you press Use The
          Suggested Lines, and only for accounts with no line yet. Your CPA decides each one.
        </InfoNote>
      </p>
      <p>
        <button type="button" onClick={suggest}>
          Use The Suggested Lines
        </button>
      </p>
      {notice && <p className="notice notice-ok">{notice}</p>}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="table-scroll">
        <table className="lines">
          <thead>
            <tr>
              <th>Account</th>
              <th>Line</th>
              <th>Suggested</th>
            </tr>
          </thead>
          <tbody>
            {map.accounts.map((account) => {
              const current = account.mapping ? `${account.mapping.part}:${account.mapping.line}` : '';
              return (
                <tr key={account.accountId}>
                  <td>
                    {account.code} {account.name}
                    {!account.isActive && <span className="muted"> · Inactive</span>}
                  </td>
                  <td>
                    <select
                      aria-label={`Line for ${account.code} ${account.name}`}
                      value={current}
                      onChange={(event) => choose(account.accountId, event.target.value)}
                    >
                      <option value="">No Line</option>
                      {map.parts.map((part) => (
                        <optgroup key={part.part} label={`Part ${part.part}: ${part.title}`}>
                          {map.lines
                            .filter((line) => line.part === part.part && fits(line.kind, account.type))
                            .map((line) => (
                              <option key={`${line.part}:${line.line}`} value={`${line.part}:${line.line}`}>
                                {line.line}. {line.label}
                              </option>
                            ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td className="muted">{account.suggested ?? ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function ReturnLinesView({ asOf, preferred, canEdit }: { asOf: string; preferred: ReturnLineForm | null; canEdit: boolean }) {
  const [form, setForm] = useState<ReturnLineForm | null>(preferred);
  const [report, setReport] = useState<ReturnLinesReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (form === null) return;
    let live = true;
    setError(null);
    api
      .returnLines(form, asOf)
      .then((result) => live && setReport(result))
      .catch((caught: unknown) => live && setError(caught instanceof ApiError ? caught.message : 'Could not build the report.'));
    return () => {
      live = false;
    };
  }, [form, asOf, version]);

  if (form === null) return <p className="muted">This business files no return with lines (the 990-N has none).</p>;

  const unplacedTies = report?.ties.filter((tie) => tie.unplacedCents !== 0) ?? [];
  const brokenTies = report?.ties.filter((tie) => !tie.ties) ?? [];

  return (
    <div className="report return-lines">
      <h2>Return Lines</h2>
      <p className="muted">
        {report ? `${FORM_TITLES[report.form]}, fiscal year ${report.year}: ${report.from} to ${report.to}. ` : ''}
        A prompt for your CPA, never a filing.{' '}
        <InfoNote label="What This Report Is">
          Each account is on one line of the return, and each line here adds up its accounts&apos; figures for the fiscal year, as the books
          hold them. Nothing is filled in or filed: your CPA types the figures into the return. An account with a figure and no line is listed
          under Not On Any Line, and an account on a line the IRS has since relabelled under Needs Confirming, so nothing goes missing
          silently. Other Changes In Net Assets are entries straight to net assets, such as an opening balance, which the return reports on
          their own line.
        </InfoNote>
      </p>

      <div className="row report-period">
        {report && report.forms.length > 1 && (
          <label>
            Form{' '}
            <select value={form} onChange={(event) => setForm(event.target.value as ReturnLineForm)}>
              {report.forms.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {FORM_TITLES[candidate]}
                </option>
              ))}
            </select>
          </label>
        )}
        {canEdit && (
          <button type="button" onClick={() => setEditing((open) => !open)}>
            {editing ? 'Close Lines' : 'Edit Lines'}
          </button>
        )}
      </div>

      {editing && <LineEditor form={form} onChanged={() => setVersion((current) => current + 1)} />}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {report && (
        <>
          {report.catalogueSentence && <p className="notice">{report.catalogueSentence}</p>}
          {report.cashBasisRefusal !== null && (
            <p role="alert" className="error">
              Column (d) cannot be worked out for this year: {report.cashBasisRefusal}
            </p>
          )}
          {unplacedTies.length > 0 && (
            <p className="notice">
              These totals leave out the accounts listed under Not On Any Line and Needs Confirming:{' '}
              {unplacedTies.map((tie) => `${tie.label}, ${formatCents(Math.abs(tie.unplacedCents))} left out`).join('; ')}.
            </p>
          )}
          {brokenTies.length > 0 && (
            <p role="alert" className="error">
              {brokenTies.map((tie) => tie.label).join(', ')} {brokenTies.length === 1 ? 'does' : 'do'} not tie to the books even with the
              unplaced accounts; report this, since the books should guarantee it.
            </p>
          )}

          {report.parts.map((part) => (
            <PartTable key={part.part} report={report} part={part} />
          ))}

          {report.needsConfirming.length > 0 && (
            <>
              <h3>Needs Confirming</h3>
              <p className="muted">The form now prints these lines under a different label. Save each line again in Edit Lines to confirm it.</p>
              <div className="table-scroll">
                <table className="lines">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Line</th>
                      <th>Was</th>
                      <th>Now</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.needsConfirming.map((account) => (
                      <tr key={account.accountId}>
                        <td>
                          {account.code} {account.name}
                        </td>
                        <td>
                          Part {account.part}, line {account.line}
                        </td>
                        <td>{account.confirmedLabel ? `${account.confirmedLabel} (${account.confirmedYear})` : `None (${account.confirmedYear})`}</td>
                        <td>{account.currentLabel}</td>
                        <td className="num">{unplacedAmount(account)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {report.notOnAnyLine.length > 0 && (
            <>
              <h3>Not On Any Line</h3>
              <div className="table-scroll">
                <table className="lines">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Why</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.notOnAnyLine.map((account) => (
                      <tr key={account.accountId}>
                        <td>
                          <Link to={linesHref(account.accountId, report, balanceKinds.has(account.type))}>
                            {account.code} {account.name}
                          </Link>
                        </td>
                        <td>{account.reason === 'no_line' ? 'No line given' : `Line ${account.mapped} is not on this year's form`}</td>
                        <td className="num">{unplacedAmount(account)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h3>Other Changes In Net Assets</h3>
          {report.otherChanges.entries.length === 0 ? (
            <p className="muted">No entry went straight to net assets this year.</p>
          ) : (
            <div className="table-scroll">
              <table className="lines">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Entry</th>
                    <th className="num">Without Donor Restrictions</th>
                    <th className="num">With Donor Restrictions</th>
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.otherChanges.entries.map((entry) => (
                    <tr key={entry.headerId}>
                      <td>{entry.date}</td>
                      <td>
                        <Link to={`/journals/${entry.headerId}`}>{entry.memo ?? 'Entry'}</Link>
                      </td>
                      <td className="num">{formatCents(entry.withoutCents)}</td>
                      <td className="num">{formatCents(entry.withCents)}</td>
                      <td className="num">{formatCents(entry.totalCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="table-scroll">
            <table className="lines" aria-label="Net Assets">
              <thead>
                <tr>
                  <th>Net Assets</th>
                  <th className="num">Without Donor Restrictions</th>
                  <th className="num">With Donor Restrictions</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ['At The Start', report.netAssets.start],
                    ['Change For The Year', report.netAssets.result],
                    ['Other Changes', report.otherChanges],
                  ] as const
                ).map(([label, row]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td className="num">{formatCents(row.withoutCents)}</td>
                    <td className="num">{formatCents(row.withCents)}</td>
                    <td className="num">{formatCents(row.totalCents)}</td>
                  </tr>
                ))}
                <tr className="report-total">
                  <td>At The End</td>
                  <td className="num">{formatCents(report.netAssets.end.withoutCents)}</td>
                  <td className="num">{formatCents(report.netAssets.end.withCents)}</td>
                  <td className="num">{formatCents(report.netAssets.end.totalCents)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {!report.netAssets.reconciles && (
            <p role="alert" className="error">
              The start, the year&apos;s change and the other changes do not add up to the end; report this, since the books should
              guarantee it.
            </p>
          )}

          {report.officers && (
            <>
              <h3>Officers</h3>
              <p className="muted">
                Each employee marked an officer, with their {report.officers.calendarYear} Form W-2.{' '}
                <InfoNote label="Which Figure The Return Wants">
                  The return asks for each officer&apos;s reportable compensation for the calendar year ending with or within the fiscal year.
                  Which W-2 box that is, and the hours and other compensation columns, are your CPA&apos;s. An officer paid as a contractor
                  is not on payroll and so not listed.
                </InfoNote>
              </p>
              {report.officers.list.length === 0 ? (
                <p className="muted">No officer was paid through payroll in {report.officers.calendarYear}.</p>
              ) : (
                <div className="table-scroll">
                  <table className="lines">
                    <thead>
                      <tr>
                        <th>Officer</th>
                        <th className="num">W-2 Box 1</th>
                        <th className="num">W-2 Box 5</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.officers.list.map((officer) => (
                        <tr key={officer.employeeId}>
                          <td>{officer.name}</td>
                          <td className="num">{formatCents(officer.box1Cents)}</td>
                          <td className="num">{formatCents(officer.box5Cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
